/**
 * AwaitingInputBanner — yellow takeover banner shown when a Member calls
 * `request_input`. Renders the prompt plus either:
 *   - a freeform textarea (no fields specified)
 *   - a structured form (one input per field declared by the Member)
 *
 * Submitting calls onAnswer with the AnswerJobInputBody shape. The "Or chat
 * freeform" link bumps focus to the chat composer so the user can answer
 * conversationally instead of via the form.
 */
import { useState } from 'react';
import { Button } from '@/components/Button/Button';
import { t } from '@/lib/i18n';
import type { AnswerJobInputBody, PendingInputField, PendingInputRequest } from '../schemas';
import styles from './AwaitingInputBanner.module.css';

export interface AwaitingInputBannerProps {
  request: PendingInputRequest;
  isSubmitting: boolean;
  onAnswer: (body: AnswerJobInputBody) => void;
  onSwitchToChat: () => void;
}

export function AwaitingInputBanner({
  request,
  isSubmitting,
  onAnswer,
  onSwitchToChat,
}: AwaitingInputBannerProps) {
  const useForm = request.fields.length > 0;

  return (
    <div className={styles.banner} role="region" aria-label={t('Aria is waiting for your input')}>
      <div className={styles.bannerHeader}>
        <span className={styles.bannerIcon} aria-hidden="true">
          ⏳
        </span>
        <span className={styles.bannerTitle}>{t('Aria is waiting for your input')}</span>
      </div>
      <p className={styles.prompt}>{request.prompt}</p>
      {useForm ? (
        <StructuredForm fields={request.fields} isSubmitting={isSubmitting} onAnswer={onAnswer} />
      ) : (
        <FreeformForm isSubmitting={isSubmitting} onAnswer={onAnswer} />
      )}
      <button type="button" className={styles.chatLink} onClick={onSwitchToChat}>
        {t('Or chat freeform ↓')}
      </button>
    </div>
  );
}

function FreeformForm({
  isSubmitting,
  onAnswer,
}: {
  isSubmitting: boolean;
  onAnswer: (body: AnswerJobInputBody) => void;
}) {
  const [text, setText] = useState('');
  const canSubmit = text.trim().length > 0 && !isSubmitting;
  return (
    <div className={styles.formBlock}>
      <textarea
        className={styles.textarea}
        rows={3}
        value={text}
        placeholder={t('Type your answer…')}
        onChange={(e) => setText(e.target.value)}
        disabled={isSubmitting}
      />
      {text.trim().length > 0 ? (
        <div className={styles.submitRow}>
          <Button
            variant="primary"
            onClick={() => onAnswer({ kind: 'freeform', text: text.trim() })}
            disabled={!canSubmit}
          >
            {t('Resume with this answer')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function StructuredForm({
  fields,
  isSubmitting,
  onAnswer,
}: {
  fields: PendingInputField[];
  isSubmitting: boolean;
  onAnswer: (body: AnswerJobInputBody) => void;
}) {
  const [values, setValues] = useState<Record<string, string | number>>({});

  const update = (name: string, value: string | number) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const required = fields.filter((f) => f.required !== false);
  const missing = required.filter((f) => {
    const v = values[f.name];
    if (typeof v === 'number') return Number.isNaN(v);
    return v === undefined || (typeof v === 'string' && v.trim() === '');
  });
  const canSubmit = missing.length === 0 && !isSubmitting;

  return (
    <div className={styles.formBlock}>
      <div className={styles.formFields}>
        {fields.map((f) => (
          <FieldRow key={f.name} field={f} value={values[f.name]} onChange={update} />
        ))}
      </div>
      <div className={styles.submitRow}>
        <Button
          variant="primary"
          onClick={() => onAnswer({ kind: 'fields', values })}
          disabled={!canSubmit}
        >
          {t('Resume with this answer')}
        </Button>
      </div>
    </div>
  );
}

function FieldRow({
  field,
  value,
  onChange,
}: {
  field: PendingInputField;
  value: string | number | undefined;
  onChange: (name: string, value: string | number) => void;
}) {
  const id = `awaiting-field-${field.name}`;
  return (
    <div className={styles.fieldRow}>
      <label htmlFor={id} className={styles.fieldLabel}>
        {field.label}
        {field.required !== false ? <span className={styles.requiredMark}>*</span> : null}
      </label>
      {field.type === 'text' || field.type === 'file' ? (
        // v1 doesn't wire file upload through the request_input answer path;
        // treat 'file' as a text field (user pastes a URL or describes the
        // file). Real upload UX is a follow-on.
        <input
          id={id}
          type="text"
          className={styles.input}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      ) : field.type === 'long_text' ? (
        <textarea
          id={id}
          className={styles.textarea}
          rows={3}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      ) : field.type === 'number' ? (
        <input
          id={id}
          type="number"
          className={styles.input}
          value={typeof value === 'number' && !Number.isNaN(value) ? value : ''}
          onChange={(e) => onChange(field.name, Number(e.target.value))}
        />
      ) : field.type === 'choice' ? (
        <div className={styles.choices}>
          {(field.choices ?? []).map((c) => (
            <label key={c} className={styles.choiceItem}>
              <input
                type="radio"
                name={id}
                value={c}
                checked={value === c}
                onChange={() => onChange(field.name, c)}
              />
              <span>{c}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}
